'use client';

import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING } from '@common/components/interaction';
import { useToast } from '@common/hooks';
import SvgChevronright from '@common/icon/SvgChevronright';
import { assert, cn } from '@core/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, useSyncExternalStore } from 'react';
import { AuthMutation, AuthQueryKey, AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { usePartnersDialog } from '../../_hooks/usePartnersDialog';
import { usePartnersMember } from '../../_hooks/usePartnersMember';
import { DEMO_FILTER, DEMO_FORM_VALUE } from '../../_utils/demo';
import { PARTNERS_LINK_FORM_ID } from '../../_utils/labels';
import { isLinkListAffectedBy, withCreatedLink } from '../../_utils/links';
import { contextOf, partnersQueriesOf } from '../../_utils/queries';
import type { PartnersLink, PartnersLinkListResponse } from '../../_utils/types';
import { PartnersDialogs } from '../PartnersDialogs';
import { PartnersShell } from '../PartnersShell';
import { ReportSection } from '../report/ReportSection';
import { ContentsPreview } from './ContentsPreview';
import { EMPTY_FORM } from './formValue';
import { DemoBanner, DemoIntro, SectionTitle, StartGuideCard } from './HomeBlocks';
import { LinkDoneDialog } from './LinkDoneDialog';
import { LinkForm } from './LinkForm';
interface DemoClickGuardProps {
  onPress: () => void;
  children: React.ReactNode;
}
const DemoClickGuard = t0 => {
  const $ = _c(5);
  const {
    onPress,
    children
  } = t0;
  let t1;
  if ($[0] !== onPress) {
    t1 = event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      onPress();
    };
    $[0] = onPress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div onPointerDownCapture={_temp} onClickCapture={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
interface PartnersHomeScreenProps {
  isGuest: boolean;
}
export const PartnersHomeScreen = t0 => {
  const $ = _c(79);
  const {
    isGuest
  } = t0;
  const {
    toastError
  } = useToast();
  const queryClient = useQueryClient();
  const openDemoDialog = usePartnersDialog(_temp2);
  const openGuideDialog = usePartnersDialog(_temp3);
  const member = usePartnersMember(isGuest);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = UnauthQueryOptions["get:/wello/v2/allows/partners/reward-prices"]();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isGuest) {
    t2 = {
      ...t1,
      select: _temp4,
      enabled: isGuest
    };
    $[1] = isGuest;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: guestUnitAmount
  } = useQuery(t2);
  const isLinkFormRequested = useSyncExternalStore(_temp5, _temp6, _temp7);
  const [isExampleOn, setIsExampleOn] = useState(null);
  const [doneLink, setDoneLink] = useState(null);
  const isDemo = isGuest || (isExampleOn ?? (!member.hasLinkYN && !isLinkFormRequested));
  let t3;
  if ($[3] !== isDemo) {
    t3 = partnersQueriesOf(isDemo);
    $[3] = isDemo;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const queries = t3;
  const isLinkFormOpened = isLinkFormRequested && !isDemo;
  let t4;
  if ($[5] !== isLinkFormOpened) {
    t4 = element => {
      if (element && isLinkFormOpened) {
        element.scrollIntoView({
          block: "start"
        });
      }
    };
    $[5] = isLinkFormOpened;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const scrollToOpenedLinkForm = t4;
  const unitAmount = (isGuest ? guestUnitAmount : member.partnerUnitAmount) ?? null;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = AuthMutation["post:/wello/v2/auth/partners/links"]();
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== queryClient) {
    t6 = t7 => {
      const {
        context: link
      } = t7;
      assert(link);
      queryClient.setQueryData(AuthQueryOptions["get:/wello/v2/auth/partners/me"]().queryKey, _temp8);
      queryClient.setQueriesData({
        queryKey: AuthQueryKey["get:/wello/v2/auth/partners/links"](),
        predicate: t8 => {
          const {
            queryKey
          } = t8;
          return isLinkListAffectedBy(queryKey, link.channel);
        }
      }, response_1 => response_1?.context && {
        ...response_1,
        context: withCreatedLink(response_1.context, link)
      });
      setDoneLink(link);
    };
    $[8] = queryClient;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== toastError) {
    t7 = error => toastError({
      error
    });
    $[10] = toastError;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = {
      ...t5,
      onSuccess: t6,
      onError: t7
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const {
    mutate: createLink,
    isPending: isCreating
  } = useMutation(t8);
  const t9 = isDemo ? DEMO_FILTER : undefined;
  let t10;
  if ($[15] !== queries) {
    t10 = filter => <ContentsPreview queries={queries} filter={filter} />;
    $[15] = queries;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== isDemo || $[18] !== queries || $[19] !== t10 || $[20] !== t9) {
    t11 = <ReportSection queries={queries} initialFilter={t9} isExample={isDemo}>{t10}</ReportSection>;
    $[17] = isDemo;
    $[18] = queries;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const report = t11;
  const t12 = isDemo ? DEMO_FORM_VALUE : EMPTY_FORM;
  let t13;
  if ($[22] !== createLink || $[23] !== isDemo || $[24] !== openDemoDialog) {
    t13 = (request, callbacks) => {
      if (isDemo) {
        openDemoDialog();
        callbacks.onSettled();
        return;
      }
      createLink({
        body: request
      }, callbacks);
    };
    $[22] = createLink;
    $[23] = isDemo;
    $[24] = openDemoDialog;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = isDemo ? "\uC608\uC2DC\uBC84\uC804\uC5D0\uC11C\uB294 \uC2E4\uC81C \uB9C1\uD06C\uB85C \uC0DD\uC131\uB418\uC9C0 \uC54A\uC544\uC694." : undefined;
  let t15;
  if ($[26] !== isCreating || $[27] !== member.suspendYN || $[28] !== queries || $[29] !== t12 || $[30] !== t13 || $[31] !== t14) {
    t15 = <LinkForm queries={queries} initialValue={t12} isSuspended={member.suspendYN} isCreating={isCreating} onCreate={t13} note={t14} />;
    $[26] = isCreating;
    $[27] = member.suspendYN;
    $[28] = queries;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const linkFormBody = t15;
  let t16;
  if ($[33] !== isDemo) {
    t16 = <SectionTitle title={"\uB0B4 \uD30C\uD2B8\uB108 \uB9C1\uD06C"} isExample={isDemo} />;
    $[33] = isDemo;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cn(FOCUS_RING, "group flex shrink-0 items-center gap-2 rounded-xxsmall font-500 text-Gray-550 transition-colors typo-body4 hover:text-Gray-700 hover:underline active:text-Gray-800 xl:hidden");
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SvgChevronright aria-hidden={true} className="size-14 transition-transform group-hover:translate-x-1 group-focus-visible:translate-x-1" />;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== openGuideDialog) {
    t19 = <button className={t17} onClick={openGuideDialog}>작성 가이드 보기{t18}</button>;
    $[37] = openGuideDialog;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t16 || $[40] !== t19) {
    t20 = <div className="flex items-center justify-between gap-8">{t16}{t19}</div>;
    $[39] = t16;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== isDemo || $[43] !== linkFormBody || $[44] !== openDemoDialog) {
    t21 = isDemo ? <DemoClickGuard onPress={openDemoDialog}>{linkFormBody}</DemoClickGuard> : linkFormBody;
    $[42] = isDemo;
    $[43] = linkFormBody;
    $[44] = openDemoDialog;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== scrollToOpenedLinkForm || $[47] !== t20 || $[48] !== t21) {
    t22 = <section ref={scrollToOpenedLinkForm} id={PARTNERS_LINK_FORM_ID} className="flex scroll-mt-72 flex-col gap-16">{t20}{t21}</section>;
    $[46] = scrollToOpenedLinkForm;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const linkForm = t22;
  let t23;
  if ($[50] !== isDemo || $[51] !== isGuest || $[52] !== openDemoDialog) {
    t23 = isDemo ? <DemoBanner actionLabel={"\uC2DC\uC791\uD558\uAE30"} onAction={isGuest ? openDemoDialog : () => setIsExampleOn(false)} /> : null;
    $[50] = isDemo;
    $[51] = isGuest;
    $[52] = openDemoDialog;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const t24 = isDemo ? "demo" : "live";
  let t25;
  if ($[54] !== isDemo || $[55] !== unitAmount) {
    t25 = isDemo ? <DemoIntro unitAmount={unitAmount} /> : <StartGuideCard unitAmount={unitAmount} onShowExample={() => setIsExampleOn(true)} />;
    $[54] = isDemo;
    $[55] = unitAmount;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== isDemo || $[58] !== linkForm || $[59] !== openDemoDialog || $[60] !== report) {
    t26 = <div className="flex flex-col gap-32 p-20 xl:gap-40 xl:p-0 xl:pb-20">{isDemo ? <><DemoClickGuard onPress={openDemoDialog}>{report}</DemoClickGuard>{linkForm}</> : <>{linkForm}{report}</>}</div>;
    $[57] = isDemo;
    $[58] = linkForm;
    $[59] = openDemoDialog;
    $[60] = report;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t24 || $[63] !== t25 || $[64] !== t26) {
    t27 = <div key={t24} className="flex flex-col xl:gap-24">{t25}{t26}</div>;
    $[62] = t24;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setDoneLink(null);
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== doneLink) {
    t29 = <LinkDoneDialog link={doneLink} onClose={t28} isCreated={true} />;
    $[67] = doneLink;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => setIsExampleOn(false);
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== isGuest || $[71] !== member.hasLinkYN || $[72] !== member.suspendYN) {
    t31 = <PartnersDialogs isGuest={isGuest} hasLinks={member.hasLinkYN} isSuspended={member.suspendYN} onExitDemo={t30} />;
    $[70] = isGuest;
    $[71] = member.hasLinkYN;
    $[72] = member.suspendYN;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t23 || $[75] !== t27 || $[76] !== t29 || $[77] !== t31) {
    t32 = <PartnersShell banner={t23}>{t27}{t29}{t31}</PartnersShell>;
    $[74] = t23;
    $[75] = t27;
    $[76] = t29;
    $[77] = t31;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  return t32;
};
function _temp(event) {
  return event.stopPropagation();
}
function _temp2(state) {
  return state.openDemoDialog;
}
function _temp3(state_0) {
  return state_0.openGuideDialog;
}
function _temp4(response) {
  return contextOf(response).partnerUnitAmount;
}
function _temp5(onChange) {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
}
function _temp6() {
  return window.location.hash === `#${PARTNERS_LINK_FORM_ID}`;
}
function _temp7() {
  return false;
}
function _temp8(response_0) {
  return response_0?.context && {
    ...response_0,
    context: {
      ...response_0.context,
      hasLinkYN: true
    }
  };
}